import { useEffect, useState } from 'react'
import { api } from '@api'
import { ConfirmDialog } from '@components/ConfirmDialog.tsx'
import styles from './Settings.module.css'

type Props = {
	open: boolean
	// The wording is the whole point of this dialog, and it turns on whether
	// anyone is left behind — so the caller keeps the button disabled until
	// the members list has actually loaded rather than guessing a default.
	soleMember: boolean
	householdName: string | null
	onClose: () => void
	onDeleted: () => void
}

export const DeleteAccountDialog = ({
	open,
	soleMember,
	householdName,
	onClose,
	onDeleted,
}: Props) => {
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// A failure from last time must not be the first thing you read on the
	// way in. ESC closes the dialog even mid-request, so this can happen.
	useEffect(() => {
		if (open) setError(null)
	}, [open])

	// "the Hewett household" mid-sentence, "The Hewett household" at the
	// start of one. A household has no name only between founding and
	// onboarding, so the fallback is rare but still has to read.
	const home = householdName
		? `the ${householdName} household`
		: 'your household'
	const Home = home.charAt(0).toUpperCase() + home.slice(1)

	const confirm = async () => {
		setBusy(true)
		setError(null)
		try {
			await api.account.remove()
			onDeleted()
		} catch (e) {
			setError(
				e instanceof Error
					? e.message
					: 'Could not delete your account',
			)
			setBusy(false)
		}
	}

	return (
		<ConfirmDialog
			open={open}
			title="Delete your account"
			destructive
			confirmLabel={busy ? 'Deleting…' : 'Delete my account'}
			busy={busy}
			onConfirm={() => void confirm()}
			onClose={onClose}
			body={
				<>
					<p>
						This will sign you out on every device and permanently
						remove your account, your personal todos and any export
						you have waiting. It cannot be undone.
					</p>
					{soleMember ? (
						<p>
							You are the only member of {home}, so it will be
							removed as well — every recipe, activity, todo, meal
							plan and photo, along with the family code used to
							join it.
						</p>
					) : (
						<p>
							{Home} will remain, and so will everything shared in
							it — recipes, activities, family todos and the meal
							plan. Only your own account and personal todos will
							be removed.
						</p>
					)}
					{error ? (
						<p className={styles.error} role="alert">
							{error}
						</p>
					) : null}
				</>
			}
		/>
	)
}
